import type { FC } from 'react';
import { memo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { cx } from '../../cx';
import { Icon } from '../../icon-svg/pc-icon';
import { ApplySpaceAiBar } from '../components/apply-space-ai';
import { ChatInput } from '../components/chat-input';
import { QuestionList } from '../components/chat-list';
import { StopButton } from '../components/stop-button';
import { useListenerUpgradeAiLimit } from '../hooks/ai-limit-store';
import { useConversationContext } from '../hooks/context';
import { useChatStatus, useConversationTools, useQuestions } from '../hooks/hook';
import { AI_PAGE_ID, ChatStatus } from '../hooks/types';
import { useChatTranslation } from '../hooks/use-i18n';
import i18n from '../i18n/i18n';
import { getEditor } from '../hooks/cache';

interface LiteChatPageProps {
  onClose?: () => void;
}

export const LiteChatPageWithI18n = memo((props: LiteChatPageProps) => {
  useListenerUpgradeAiLimit();

  return (
    <I18nextProvider i18n={i18n}>
      <LiteChatPage {...props} />
    </I18nextProvider>
  );
});

export const LiteChatPage: FC<LiteChatPageProps> = (props) => {
  const { onClose } = props;
  const questions = useQuestions();
  const { conversationId, onSummarize, navbar } = useConversationContext();
  const status = useChatStatus(conversationId);
  const isLoading = status === ChatStatus.SENDING;
  const isDefaultPage = conversationId === AI_PAGE_ID;
  const { t } = useChatTranslation();
  const { summarize } = useConversationTools();

  return (
    <div className={cx('flex flex-col h-full max-w-3xl mx-auto w-full text-t2', AI_PAGE_ID)}>
      {onClose && (
        <div className="flex items-center justify-end p-3">
          <Icon name="IcClose" size="small" onClick={onClose} />
        </div>
      )}
      {navbar?.()}
      <div className="flex-1 overflow-hidden">
        {isDefaultPage && (
          <div className="px-6 space-x-2 flex">
            <Icon name="IcAiFill" className="mt-1 text-ai_color" size="middle" />
            <div>{t('chatAiIntroduction')}</div>
          </div>
        )}
        <QuestionList questions={questions} className="h-full pb-10 px-6" />
      </div>
      <div className="relative flex-shrink-0 pt-0 p-6 space-y-2">
        <ApplySpaceAiBar isCol provider="lite-chat" />
        <div className="flex items-center pt-2 text-t4 space-x-1">
          {[
            {
              icon: 'IcSummary' as const,
              text: t('chatHomeSummarize'),
              onClick: () => {
                if (onSummarize) {
                  onSummarize();
                } else {
                  summarize();
                }
              },
            },
            {
              icon: 'IcMagicWand' as const,
              text: t('chatHomeWrite'),
              onClick: () => {
                const editor = getEditor(conversationId);
                if (editor) {
                  editor.commands.insertContent(t('chatHomeHelpMeWrite'));
                  editor.commands.focus();
                }
              },
            },
          ].map((item, index) => (
            <div
              key={index}
              className="px-2 py-1 rounded-full bg-black_003 animate-hover animate-click flex items-center space-x-1"
              onClick={item.onClick}
            >
              <Icon name={item.icon} size="xsmall" className="text-ai_color" />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
        <div
          className="bg-white2 px-4 py-2 rounded-lg border border-normal/15 transition-border focus-within:border-normal/35"
          style={{
            boxShadow: '0px 4px 12px 0px #0000000D',
          }}
        >
          {isLoading && <StopButton />}
          <ChatInput />
        </div>
      </div>
    </div>
  );
};
